import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/app/admin/_common/ui/dialog";
import { Sheet, SheetContent, SheetTitle } from "@/app/admin/_common/ui/sheet";
import CuoButton from "@/app/admin/_common/CuoButton";
import { FestivalForm } from "./FestivalForm";
import { useFestivalFormFlow } from "./useFestivalFormFlow";
import FestivalService, { Festival } from "@/app/_services/FestivalService";
import { PartyPopper, Eye, EyeOff, X } from "lucide-react";
import { toast } from "@/app/_utils/toast";
import { useIsMobile } from "@/app/admin/_hooks/useIsMobile";

export const FestivalDialog = ({
  defaultFestival,
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  defaultFestival?: Festival | null;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const flow = useFestivalFormFlow({
    defaultFestival,
    onSaved: () => {
      onSaved();
      onClose();
    },
  });
  const { control, errors, isSaving, handleSave, setSelectedImage } = flow;
  const [isPublishing, setIsPublishing] = useState(false);

  async function handleTogglePublish() {
    if (!defaultFestival) return;
    setIsPublishing(true);
    try {
      await FestivalService.publishFestivalAsync(defaultFestival.id, !defaultFestival.published);
      toast.success(defaultFestival.published ? "Festival unpublished" : "Festival published");
      onSaved();
      onClose();
    } catch (e) {
      toast.error("Failed to update festival");
    } finally {
      setIsPublishing(false);
    }
  }

  const isMobile = useIsMobile();
  const title = defaultFestival ? defaultFestival.title : "New Festival";

  const form = (
    <FestivalForm
      control={control}
      errors={errors}
      defaultFestival={defaultFestival}
      onImageSelect={setSelectedImage}
    />
  );

  const footerButtons = (
    <>
      {defaultFestival && (
        <CuoButton
          variant="outline"
          disableOnAsync={isPublishing}
          icon={
            defaultFestival.published ? (
              <EyeOff className="size-3.5" />
            ) : (
              <Eye className="size-3.5" />
            )
          }
          label={defaultFestival.published ? "Unpublish" : "Publish"}
          onClick={handleTogglePublish}
          className="mr-auto"
        />
      )}
      <CuoButton variant="outline" label="Cancel" onClick={onClose} />
      <CuoButton disableOnAsync={isSaving} variant="default" onClick={handleSave} label="Save" />
    </>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
        <SheetContent
          side="right"
          hideClose
          className="flex h-full w-screen max-w-none flex-col gap-0 border-l-0 bg-white p-0 sm:max-w-none"
        >
          <SheetTitle className="sr-only">{title}</SheetTitle>
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <span className="inline-flex items-center gap-2 text-[13px] font-normal text-foreground">
              <PartyPopper className="size-4 flex-none text-muted-foreground" />
              {title}
            </span>
            <button
              type="button"
              onClick={onClose}
              className="flex size-6 flex-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
              aria-label="Close"
            >
              <X className="size-3.5" />
            </button>
          </div>

          <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
            <p className="text-[12.5px] text-muted-foreground">
              Group shows into a festival, set the title and description, then publish when ready.
            </p>
            {form}
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-border p-3">
            {footerButtons}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="inset-0 left-0 top-0 m-auto h-fit max-h-[85vh] w-full max-w-[480px] translate-x-0 translate-y-0 bg-white">
        <DialogHeader>
          <DialogTitle>
            <span className="inline-flex items-center gap-2 align-middle">
              <PartyPopper className="size-4 flex-none text-muted-foreground" />
              {title}
            </span>
          </DialogTitle>
          <DialogDescription>
            Group shows into a festival, set the title and description, then publish when ready.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto py-2">{form}</div>

        <DialogFooter>{footerButtons}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
